import type {
  BriqpayCreateSessionRequest,
  BriqpayCustomerType,
  BriqpayHook,
  BriqpayModuleSettings,
  BriqpayModulesConfig,
  BriqpaySession,
  CountryCode
} from 'ecosystem'
import { CustomerType } from 'ecosystem'
import { briqpayApi } from 'api'
import {
  DEFAULT_COUNTRY,
  DEFAULT_CURRENCY,
  getFullLocale,
  IS_PRODUCTION,
  resolveUrlPath
} from 'shared-utils'
import {
  briqpayOrderFactory,
  briqpayOrderLineFactory,
  briqpayShippingLine
} from 'api/utils/briqpay/checkout'
import { runCreateOrder } from './runCreateOrder'
import {
  type BuildPayloadArgs,
  type CreateOrderContext,
  type CreateOrderHandlerOpts,
  type CreateOrderRouteResponse,
  type PaymentProviderAdapter
} from './types'

/**
 * Business customers get Briqpay's B2B flow — organisation-number lookup and the invoice
 * and leasing PSPs that only accept companies. Everyone else gets the consumer flow.
 * Fixed at create: a PATCH cannot change it.
 */
const resolveBriqpayCustomerType = (opts: CreateOrderHandlerOpts): BriqpayCustomerType =>
  opts.paymentProviderConfig?.providerOptions?.briqpay?.customerType ??
  (opts.customerType === CustomerType.BUSINESS ? 'business' : 'consumer')

/**
 * Build `modules` from the store settings.
 *
 * The module layout only varies for business sessions — Briqpay renders consumer sessions
 * payment-only regardless — so `mod` is supplied for B2B and omitted otherwise. Decisions
 * is the exception: it configures the payment module, which every session renders, so it
 * is applied for both customer types.
 *
 * Only emit keys that are actually set: `modules` is a strict schema and an unknown or
 * misplaced field is rejected with a 400 naming its exact path.
 */
const briqpayModulesFactory = (
  mod: BriqpayModuleSettings | undefined,
  decisionEnabled: boolean | undefined
): BriqpayModulesConfig => {
  const config: NonNullable<BriqpayModulesConfig['config']> = {}

  if (decisionEnabled) {
    config.payment = { decision: { enabled: true } }
  }
  if (mod?.companyPrefillLock) {
    config.company_lookup = { companyPrefillLock: mod.companyPrefillLock }
  }
  if (mod?.billingLockFields) {
    config.billing = { lockFields: mod.billingLockFields }
  }
  if (mod?.shippingLockFields) {
    config.shipping = { lockFields: mod.shippingLockFields }
  }
  if (mod?.termsCheckboxes?.length) {
    config.terms = { checkboxes: mod.termsCheckboxes }
  }
  if (mod?.orderNote) {
    config.order_note = mod.orderNote
  }
  if (mod?.upsell) {
    config.upsell = mod.upsell
  }

  return {
    ...(mod?.loadModules?.length ? { loadModules: mod.loadModules } : {}),
    ...(Object.keys(config).length ? { config } : {})
  }
}

/**
 * Briqpay hooks are configured per session, so our SF order number rides along in the URL
 * the same way the other providers' push URLs carry it, and they post to the same shared
 * `/api/orders/push`. The body is never read: the endpoint provisions by order number, and
 * `provisionOrderBriqpayProvider` re-reads the session to decide what the hook actually
 * meant — provision, no-op, or (on a rejection) walk the order back to CANCELLED.
 */
const briqpayHooksFactory = (pushDomain: string, orderNumber?: string | null): BriqpayHook[] => {
  const url = resolveUrlPath(
    pushDomain,
    `/api/orders/push?order_id=${encodeURIComponent(orderNumber ?? '')}`
  )

  return [
    { eventType: 'session_status', statuses: ['completed'], method: 'POST', url },
    {
      eventType: 'order_status',
      statuses: [
        'order_pending',
        'order_rejected',
        'order_cancelled',
        'order_approved_not_captured'
      ],
      method: 'POST',
      url
    }
  ]
}

const buildBriqpayPayload = ({
  ctx,
  opts,
  backendOrder,
  sessionCountry,
  configCountry,
  shippingInfo
}: BuildPayloadArgs<CountryCode>): BriqpayCreateSessionRequest => {
  const briqpayOptions = opts.paymentProviderConfig?.providerOptions?.briqpay
  const { items, cartDiscountCode, cartStateUuid } = ctx.payload
  const customerType = resolveBriqpayCustomerType(opts)
  // Only B2B sessions have a module layout to configure — consumer sessions are always
  // payment-only — so the settings are applied for business and skipped otherwise.
  const moduleSettings = customerType === 'business' ? briqpayOptions?.business : undefined

  const { orderLines } = briqpayOrderLineFactory(
    items,
    cartDiscountCode,
    configCountry,
    opts.buyerVat
  )
  // Briqpay cannot render a shipping-method list at all — its `shipping` module collects
  // an address, not a rate — so freight always rides along as a `shipping_fee` cart row.
  const cart = shippingInfo ? [...orderLines, briqpayShippingLine(shippingInfo)] : orderLines
  // Webhooks must reach a public host; use a mock domain in dev, as Qliro does.
  const pushDomain = IS_PRODUCTION ? opts.redirectDomain : 'https://granditude.com'

  return {
    product: {
      type: 'payment',
      intent: 'payment_one_time',
      ...(briqpayOptions?.variantId ? { variantId: briqpayOptions.variantId } : {})
    },
    customerType,
    country: sessionCountry || DEFAULT_COUNTRY,
    locale: getFullLocale(ctx.locale, { no: 'nb-NO', en: 'en-GB' }).toLowerCase(),
    urls: {
      terms: resolveUrlPath(opts.redirectDomain, briqpayOptions?.termsUrl ?? '/terms'),
      // `urls.redirect` has no template support, the same limitation as Svea's
      // ConfirmationUri — so the SF order number is hardcoded here and the Briqpay
      // sessionId is recovered from the order's persisted `paymentId`.
      redirect: resolveUrlPath(
        opts.redirectDomain,
        `/confirmation?order_id=${encodeURIComponent(backendOrder.orderNumber ?? '')}`
      ),
      ...(briqpayOptions?.withdrawalAndReturnsUrl
        ? {
            withdrawalAndReturns: resolveUrlPath(
              opts.redirectDomain,
              briqpayOptions.withdrawalAndReturnsUrl
            )
          }
        : {})
    },
    references: {
      // The conventional order reference most PSPs behind Briqpay surface.
      reference1: backendOrder.orderNumber ?? '',
      // Replaces Qliro's MerchantProvidedMetadata — the confirmation page matches on it
      // before clearing the cart.
      ...(cartStateUuid ? { cartStateUuid } : {})
    },
    hooks: briqpayHooksFactory(pushDomain, backendOrder.orderNumber),
    config: {
      ...(briqpayOptions?.disableSessionCompleteRedirect
        ? { disableSessionCompleteRedirect: true }
        : {}),
      ...(briqpayOptions?.realTimeProcessing ? { realTimeProcessing: true } : {})
    },
    data: {
      order: briqpayOrderFactory(cart, briqpayOptions?.purchaseCurrency || DEFAULT_CURRENCY)
    },
    modules: briqpayModulesFactory(moduleSettings, briqpayOptions?.decisionEnabled)
  }
}

const briqpayAdapter: PaymentProviderAdapter<
  BriqpaySession,
  BriqpayCreateSessionRequest,
  CountryCode
> = {
  name: 'BRIQPAY',
  configCountry: (opts) => opts.paymentProviderConfig?.providerOptions?.briqpay?.countryCode,
  availableCountries: (opts) =>
    opts.paymentProviderConfig?.providerOptions?.briqpay?.availableCountries,
  orderLineFactory: briqpayOrderLineFactory,
  buildPayload: buildBriqpayPayload,
  createSession: (payload) => briqpayApi.checkout.createSession(payload),
  respond: (briqpayResponse) => ({ briqpayResponse }),
  paymentIdOf: (session) => session.sessionId
}

export const briqpayPaymentProvider = (
  ctx: CreateOrderContext,
  opts: CreateOrderHandlerOpts
): Promise<CreateOrderRouteResponse> => runCreateOrder(ctx, opts, briqpayAdapter)
